import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { UIMessagePart } from 'ai';
import type { DBMessage } from '@/lib/db/chat-schema';
import type { ChatMessage, ChatTools, CustomUIDataTypes } from '@/lib/ai/types';
import type { GuardrailsResult } from '@/lib/ai/guardrails/types';
import { formatISO } from 'date-fns';
import {
  UNAVAILABLE_INBOUND_PART,
  isUnavailableInboundPlaceholderText,
} from '@/lib/whatsapp/unavailable-inbound-message-constants';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function sanitizeText(text: string) {
  return text.replace('<has_function_call>', '');
}

/**
 * Removes inline prompt anchor markers (`[[...]]`, e.g. [[ws1]], [[g1]], [[w12]]) from display text.
 * Also drops horizontal spaces/tabs immediately after each `]]`, and trims trailing spaces at line ends.
 * Preserves newlines so pre-wrap / bullet lists stay readable.
 */
export function stripPromptAnchorMarkers(text: string): string {
  if (!text) return '';
  return text
    .replace(/\[\[[^\]]+\]\][ \t]*/g, '')
    .replace(/[ \t]+(?=\n|$)/gm, '');
}

function normalizeUnavailableInboundParts(
  parts: unknown[],
  role: string,
): UIMessagePart<CustomUIDataTypes, ChatTools>[] {
  const normalized = parts as UIMessagePart<CustomUIDataTypes, ChatTools>[];

  return normalized.map((part) => {
    const partAny = part as { type: string; text?: string };
    if (partAny.type === UNAVAILABLE_INBOUND_PART) {
      return part;
    }
    if (
      role === 'user' &&
      partAny.type === 'text' &&
      isUnavailableInboundPlaceholderText(partAny.text)
    ) {
      return { type: UNAVAILABLE_INBOUND_PART } as unknown as UIMessagePart<
        CustomUIDataTypes,
        ChatTools
      >;
    }
    return part;
  });
}

export type DBMessageWithGuardrails = DBMessage & {
  guardrails?: GuardrailsResult;
};

export type UIMessageConvertInput = {
  id: string;
  role: string;
  parts: unknown;
  createdAt: Date | string;
  guardrails?: GuardrailsResult;
  guardrailsStatus?: 'running';
};

export function convertToUIMessages(messages: UIMessageConvertInput[]): ChatMessage[] {
  const result = messages.map((message) => {
    const role = message.role as 'user' | 'assistant' | 'system' | 'human-assistant';

    const uiMessage = {
      id: message.id,
      role,
      parts: normalizeUnavailableInboundParts(
        message.parts as unknown[],
        role,
      ),
      metadata: {
        createdAt: formatISO(
          message.createdAt instanceof Date
            ? message.createdAt
            : new Date(message.createdAt),
        ),
        guardrails: message.guardrails,
        guardrailsStatus: message.guardrailsStatus,
      },
    };
    
    return uiMessage;
  });
  
  return result;
}

export function getTextFromMessage(message: ChatMessage): string {
  return message.parts
    .filter((part) => part.type === 'text')
    .map((part) => part.text)
    .join('');
}

export type MergeAdjacentTextMessagePartsOptions = {
  /**
   * When true, keep a final empty `reasoning` part so the chat UI can still show the
   * thinking indicator. Empty reasoning between text chunks is never kept (it only bridges merges).
   */
  isStreaming?: boolean;
};

function isEmptyReasoningPart(part: { type: string; text?: string }): boolean {
  return part.type === 'reasoning' && (!part.text || !String(part.text).trim());
}

/**
 * Merge `text` parts for display when the model splits one reply into multiple chunks
 * (e.g. Vertex inserts empty `reasoning` between segments). Purely consecutive text is
 * merged; text separated only by empty reasoning is merged too.
 */
export function mergeAdjacentTextMessageParts<
  T extends { type: string; text?: string },
>(parts: T[], options?: MergeAdjacentTextMessagePartsOptions): T[] {
  const isStreaming = options?.isStreaming ?? false;
  const result: T[] = [];
  const run: T[] = [];

  const flush = () => {
    if (run.length === 0) return;
    if (run.length === 1) {
      result.push(run[0]);
    } else {
      const combinedText = run
        .map((p) => (typeof p.text === 'string' ? p.text : ''))
        .join('');
      const last = run[run.length - 1];
      const anyInFlight = run.some((p) => {
        const s = 'state' in p ? (p as { state?: string }).state : undefined;
        return s != null && s !== 'done';
      });
      result.push({
        ...last,
        text: combinedText,
        ...(anyInFlight ? { state: 'streaming' as const } : {}),
      } as T);
    }
    run.length = 0;
  };

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (part.type === 'text') {
      run.push(part);
      continue;
    }
    if (isEmptyReasoningPart(part)) {
      const isLast = i === parts.length - 1;
      if (isLast && isStreaming) {
        flush();
        result.push(part);
      }
      // Omit other empty reasoning: bridges text runs or trailing when not streaming
      continue;
    }
    flush();
    result.push(part);
  }
  flush();
  return result;
}

export const fetcher = async (url: string) => {
  const response = await fetch(url, {
    // Prevent browser caching to ensure fresh data
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// Helper function to get translated error message
function getTranslatedErrorMessage(errorType: string, fallbackMessage: string): string {
  // Only run in browser environment
  if (typeof window === 'undefined') {
    return fallbackMessage;
  }
  
  // Note: i18n translation removed to avoid React context import in server routes
  // Fallback message is used instead
  
  return fallbackMessage;
}

export async function fetchWithErrorHandlers(
  input: RequestInfo | URL,
  init?: RequestInit,
) {
  try {
    const response = await fetch(input, init);

    if (!response.ok) {
      // Try to extract error message and error type from response body
      let errorMessage = `HTTP error! status: ${response.status}`;
      let errorType: string | undefined;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json().catch(() => null);
          if (errorData && typeof errorData === 'object') {
            if (errorData.message) {
              errorMessage = errorData.message;
            }
            if (errorData.errorType) {
              errorType = errorData.errorType;
            }
          }
        }
      } catch {
        // If parsing fails, use default error message
      }
      
      // If we have an errorType, try to get translated message
      if (errorType) {
        errorMessage = getTranslatedErrorMessage(errorType, errorMessage);
      }
      
      const error = new Error(errorMessage);
      throw error;
    }

    return response;
  } catch (error: unknown) {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw new Error('No internet connection');
    }

    throw error;
  }
}

/**
 * Formats a phone number string to include + sign and dashes
 * Examples:
 * - "1234567890" -> "+1-234-567-8900"
 * - "+1234567890" -> "+1-234-567-8900"
 * - "972501234567" -> "+972-50-123-4567"
 */
export function formatPhoneNumber(phone: string): string {
  if (!phone) return phone;
  
  // Remove all non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // If it doesn't start with +, assume it needs one
  if (!cleaned.startsWith('+')) {
    // If it starts with 0, remove it (common in some countries)
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    // Add + prefix
    cleaned = '+' + cleaned;
  }
  
  // Extract country code (1-3 digits after +)
  const match = cleaned.match(/^\+(\d{1,3})(.*)$/);
  if (!match) return phone; // Return original if format is unexpected
  
  const countryCode = match[1];
  const number = match[2];
  
  // Format based on country code length
  if (countryCode.length === 1) {
    // US/Canada: +1-234-567-8900
    if (number.length === 10) {
      return `+${countryCode}-${number.slice(0, 3)}-${number.slice(3, 6)}-${number.slice(6)}`;
    }
  } else if (countryCode.length === 2) {
    // Israel, UK, etc.: +972-50-123-4567
    if (number.length >= 8) {
      const remaining = number;
      const part1 = remaining.slice(0, 2);
      const part2 = remaining.slice(2, 5);
      const part3 = remaining.slice(5);
      return `+${countryCode}-${part1}-${part2}-${part3}`;
    }
  } else if (countryCode.length === 3) {
    // 3-digit country codes
    if (number.length >= 7) {
      const remaining = number;
      const part1 = remaining.slice(0, Math.min(3, remaining.length));
      const part2 = remaining.slice(part1.length, part1.length + 3);
      const part3 = remaining.slice(part1.length + part2.length);
      return `+${countryCode}-${part1}-${part2}-${part3}`;
    }
  }
  
  // Fallback: just add dashes every 3 digits
    const formatted = number.match(/.{1,3}/g)?.join('-') || number;
  return `+${countryCode}-${formatted}`;
}

export function detectPrimaryLanguage(text: string): 'he' | 'en' {
  if (!text) return 'he';
  
  let heCount = 0;
  let enCount = 0;
  
  for (let i = 0; i < text.length; i++) {
    const charCode = text.charCodeAt(i);
    // Hebrew characters range: 0x0590 to 0x05FF
    if (charCode >= 0x0590 && charCode <= 0x05FF) {
      heCount++;
    } 
    // English characters: A-Z (65-90) and a-z (97-122)
    else if ((charCode >= 65 && charCode <= 90) || (charCode >= 97 && charCode <= 122)) {
      enCount++;
    }
  }
  
  return heCount >= enCount ? 'he' : 'en';
}

/**
 * Normalizes a phone number for search purposes
 * Removes all non-digit characters, handles country codes, and removes leading zeros
 * Examples:
 * - "+1-234-567-8900" -> "12345678900"
 * - "972-50-123-4567" -> "972501234567"
 * - "050-123-4567" -> "972501234567" (Israeli format: 0 -> 972)
 * - "1234567890" -> "1234567890"
 */
export function normalizePhoneForSearch(phone: string): string {
  if (!phone) return '';
  
  // Remove all non-digit characters
  let cleaned = phone.replace(/\D/g, '');
  
  if (!cleaned) return '';
  
  // Handle Israeli format: numbers starting with 0 should be converted to 972
  // But only if it's a valid Israeli mobile number (starts with 05)
  if (cleaned.startsWith('0') && cleaned.length >= 9) {
    // Remove leading 0 and add 972
    cleaned = '972' + cleaned.substring(1);
  }
  
  return cleaned;
}

/**
 * Anonymous web-embed sessions use generated IDs (see embed chat/message routes).
 * JWT signed-in users keep the stable customer id from the token.
 */
export function isWebEmbedAnonymousCustomerId(customerId: string): boolean {
  return customerId.startsWith('anon_') || customerId.startsWith('embed_');
}

/**
 * Friendly label for Messenger PSIDs when no profile name is available.
 */
export function formatMessengerContactId(psid: string): string {
  if (!psid) return psid;
  const suffix = psid.length > 4 ? psid.slice(-4) : psid;
  return `Messenger contact ${suffix}`;
}

/**
 * Friendly label for Instagram scoped IDs when no profile name is available.
 */
export function formatInstagramContactId(igsid: string): string {
  if (!igsid) return igsid;
  const suffix = igsid.length > 4 ? igsid.slice(-4) : igsid;
  return `Instagram contact ${suffix}`;
}

/**
 * Last digits already present in an anonymous embed customerId
 * (`embed_<slug>_<Date.now()>`). Inbox digit search can match this without
 * hashing or persisting a separate label.
 */
export function getAnonymousWebChatDisplayNumber(customerId: string): string {
  const digits = customerId.replace(/\D/g, '');
  if (digits.length >= 5) return digits.slice(-5);
  if (digits.length > 0) return digits;
  const compact = customerId.replace(/[^a-zA-Z0-9]/g, '');
  return compact.slice(-5) || customerId;
}

/**
 * Formats a customer ID based on chat type
 * - For WhatsApp: formats as phone number
 * - For web-embed (anonymous): 'Web chat #<last-5-digits>' from customerId
 * - For web-embed (signed-in / JWT): raw customerId
 * - For Messenger: friendly contact label with last 4 PSID digits
 * - For other types: returns as-is
 */
export function formatCustomerId(customerId: string, chatType: string): string {
  if (!customerId) return customerId;
  
  if (chatType === 'web-embed') {
    if (!isWebEmbedAnonymousCustomerId(customerId)) {
      return customerId;
    }
    return `Web chat #${getAnonymousWebChatDisplayNumber(customerId)}`;
  }
  
  if (chatType === 'whatsapp') {
    return formatPhoneNumber(customerId);
  }

  if (chatType === 'messenger') {
    return formatMessengerContactId(customerId);
  }

  if (chatType === 'instagram') {
    return formatInstagramContactId(customerId);
  }

  // For other types (test, etc.), return as-is
  return customerId;
}
